import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { SALE_REOPEN_REQUEST_MODAL } from "../config/sale-reopen-request-modal.config.js";
import { reopenRequestSchema } from "../schemas/reopen-request.schema.js";
import { useSaleReopen } from "./use-sale-reopen.js";

/**
 * RHF + Zod + carga de destinatários + POST request-reopen.
 *
 * @param {Object} params
 * @param {string|number|null} params.idsale
 * @param {boolean} params.open
 * @param {(response: Object) => void} [params.onSuccess]
 * @param {(response: Object) => void} [params.onError]
 */
export function useSaleReopenRequestForm({
  idsale,
  open,
  onSuccess,
  onError,
}) {
  const { loadRecipientSelectionOptions, requestReopen, loading } =
    useSaleReopen();
  const optionsErrorFallback =
    SALE_REOPEN_REQUEST_MODAL.recipient.errorFallback;

  const [options, setOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState(null);

  const form = useForm({
    resolver: zodResolver(reopenRequestSchema),
    mode: "onChange",
    defaultValues: {
      recipientToken: "",
      requesterReason: "",
    },
  });

  const applyOptionsResponse = useCallback(
    (response) => {
      if (response?.success) {
        setOptions(Array.isArray(response.data) ? response.data : []);
        setOptionsError(null);
      } else {
        setOptions([]);
        setOptionsError(response?.message || optionsErrorFallback);
      }
    },
    [optionsErrorFallback],
  );

  const loadOptions = useCallback(async () => {
    setOptionsLoading(true);
    setOptionsError(null);
    const response = await loadRecipientSelectionOptions();
    applyOptionsResponse(response);
    setOptionsLoading(false);
    return response;
  }, [applyOptionsResponse, loadRecipientSelectionOptions]);

  useEffect(() => {
    if (!open) return undefined;

    form.reset({ recipientToken: "", requesterReason: "" });
    let cancelled = false;

    (async () => {
      setOptionsLoading(true);
      setOptionsError(null);
      const response = await loadRecipientSelectionOptions();
      if (cancelled) return;
      applyOptionsResponse(response);
      setOptionsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset/load only when modal opens
  }, [open]);

  const onSubmit = form.handleSubmit(async (values) => {
    if (idsale == null) return;

    const response = await requestReopen(idsale, {
      recipientToken: values.recipientToken,
      requesterReason: values.requesterReason || undefined,
    });

    if (response?.success) {
      onSuccess?.(response);
      return;
    }

    onError?.(response);
  });

  const { isValid } = form.formState;
  const isSubmitting = loading && !optionsLoading;

  return {
    form,
    options,
    optionsLoading,
    optionsError,
    reloadOptions: loadOptions,
    onSubmit,
    isSubmitting,
    canSubmit: isValid && !optionsLoading && !isSubmitting && Boolean(idsale),
  };
}
